// lib/deploy/serviceWindow.ts
//
// ¿El local está EN SERVICIO ahora? Lo usa BuildWatcher (S5-1, P1-15) para no
// recargar una tablet de operador por un deploy normal mientras el local
// atiende. Con 40-50 deploys al día, recargar "a los 90 s sin toques" eran ~40
// arranques en frío por tablet y por día — en plena cena.
//
// Fuente: `locations.operating_hours` + `locations.timezone` (el mismo horario
// del micrositio y de las alertas de salud), vía /api/build-id/schedule.
// Reusa openStateNow (lib/health/openHours.ts), que ya resuelve días cerrados,
// horarios a medias y locales que cruzan medianoche.
//
// - Con horario: en servicio si está abierto ahora o lo estará/estuvo dentro de
//   SERVICE_MARGIN_MIN (preparación antes de abrir y cierre de caja después).
// - Sin horario utilizable: en servicio SALVO en la ventana nocturna
//   NIGHT_WINDOW (03:00–05:00 hora del local; si no hay zona, la del
//   dispositivo, que está físicamente en el local).
//
// Puro (sin DOM ni red) para probarse en jest-node.
import { formatInTimeZone } from "date-fns-tz"
import { openStateNow } from "@/lib/health/openHours"
import { AGENT_SLUG_REGEX, isReservedSlug } from "@/lib/slug/validateSlug"

export const SERVICE_MARGIN_MIN = 30
export const NIGHT_WINDOW = { startHour: 3, endHour: 5 } as const

export interface LocationSchedule {
  timezone: string | null
  /** jsonb `locations.operating_hours` tal cual (puede venir null o a medias). */
  operatingHours: unknown
}

function isValidTimezone(tz: string | null | undefined): tz is string {
  if (!tz || !tz.trim()) return false
  try {
    formatInTimeZone(new Date(0), tz, "H")
    return true
  } catch {
    return false
  }
}

function resolveTimezone(...candidates: Array<string | null | undefined>): string {
  for (const tz of candidates) if (isValidTimezone(tz)) return tz.trim()
  return "UTC"
}

export function isInService(a: {
  schedule: LocationSchedule | null
  nowMs: number
  /** Intl.DateTimeFormat().resolvedOptions().timeZone del dispositivo. */
  deviceTimezone?: string
}): boolean {
  const tz = resolveTimezone(a.schedule?.timezone, a.deviceTimezone)

  if (a.schedule) {
    const stateAt = (t: number) =>
      openStateNow({ operatingHours: a.schedule!.operatingHours, timezone: tz, nowMs: t }).state
    const now = stateAt(a.nowMs)
    if (now !== "unknown") {
      if (now === "open") return true
      const margin = SERVICE_MARGIN_MIN * 60_000
      return stateAt(a.nowMs + margin) === "open" || stateAt(a.nowMs - margin) === "open"
    }
  }

  const hour = Number(formatInTimeZone(new Date(a.nowMs), tz, "H"))
  return !(hour >= NIGHT_WINDOW.startHour && hour < NIGHT_WINDOW.endHour)
}

/** Raíces cuyo SEGUNDO segmento es el slug del local. */
const SLUG_IN_SECOND_SEGMENT = new Set(["display", "door"])

/**
 * Slug del local de una ruta de operador, o null si la ruta no es de un local
 * (superadmin, employee, onboarding…). Un null o un slug que no existe solo
 * significa "sin horario" → ventana nocturna; nunca rompe nada.
 */
export function locationSlugFromPathname(pathname: string | null | undefined): string | null {
  if (!pathname) return null
  const segments = pathname.split("/").filter(Boolean).map((s) => s.toLowerCase())
  if (segments.length === 0) return null
  const candidate = SLUG_IN_SECOND_SEGMENT.has(segments[0]) ? segments[1] : segments[0]
  if (!candidate || !AGENT_SLUG_REGEX.test(candidate)) return null
  if (!SLUG_IN_SECOND_SEGMENT.has(segments[0]) && isReservedSlug(candidate)) return null
  return candidate
}
